import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import websocketService from '../../services/websocketService';

// Async thunk для подключения к WebSocket
export const connectWebSocket = createAsyncThunk(
  'websocket/connect',
  async (token, { rejectWithValue }) => {
    try {
      websocketService.connect(token);
      return { status: 'connecting' };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk для отключения от WebSocket
export const disconnectWebSocket = createAsyncThunk(
  'websocket/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      websocketService.disconnect();
      return { status: 'disconnected' };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const websocketSlice = createSlice({
  name: 'websocket',
  initialState: {
    isConnected: false,
    isConnecting: false,
    reconnectAttempts: 0,
    maxReconnectAttempts: 5,
    error: null,
    onlineUsers: [], // Список онлайн пользователей
    connectionStatus: 'disconnected', // disconnected, connecting, connected, error
  },
  reducers: {
    // Обновление статуса подключения
    updateConnectionStatus: (state, action) => {
      const { status, error } = action.payload;
      state.connectionStatus = status;
      state.isConnected = status === 'connected';
      state.isConnecting = status === 'connecting';
      
      if (error) {
        state.error = error;
      } else {
        state.error = null;
      }
    },

    // Обновление статуса пользователя (онлайн/офлайн)
    updateUserStatus: (state, action) => {
      const { userId, status } = action.payload;
      
      if (status === 'online') {
        // Добавляем пользователя в список онлайн, если его там нет
        if (!state.onlineUsers.find(user => user.id === userId)) {
          state.onlineUsers.push({ id: userId, status: 'online' });
        }
      } else if (status === 'offline') {
        // Удаляем пользователя из списка онлайн
        state.onlineUsers = state.onlineUsers.filter(user => user.id !== userId);
      }
    },

    // Обновление счетчика попыток переподключения
    updateReconnectAttempts: (state, action) => {
      state.reconnectAttempts = action.payload;
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetWebSocketState: (state) => {
      state.isConnected = false;
      state.isConnecting = false;
      state.reconnectAttempts = 0;
      state.error = null;
      state.onlineUsers = [];
      state.connectionStatus = 'disconnected';
    },
  },
  extraReducers: (builder) => {
    builder
      // Подключение к WebSocket
      .addCase(connectWebSocket.pending, (state) => {
        state.isConnecting = true;
        state.connectionStatus = 'connecting';
        state.error = null;
      })
      .addCase(connectWebSocket.fulfilled, (state, action) => {
        state.isConnecting = false;
        state.connectionStatus = action.payload.status;
      })
      .addCase(connectWebSocket.rejected, (state, action) => {
        state.isConnecting = false;
        state.connectionStatus = 'error';
        state.error = action.payload;
      })
      
      // Отключение от WebSocket
      .addCase(disconnectWebSocket.fulfilled, (state, action) => {
        state.isConnected = false;
        state.isConnecting = false;
        state.connectionStatus = 'disconnected';
        state.onlineUsers = [];
        state.error = null;
      })
      .addCase(disconnectWebSocket.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

// Экспортируем actions
export const {
  updateConnectionStatus,
  updateUserStatus,
  updateReconnectAttempts,
  clearError,
  resetWebSocketState,
} = websocketSlice.actions;

// Селекторы
export const selectWebSocketStatus = (state) => state.websocket.connectionStatus;
export const selectIsWebSocketConnected = (state) => state.websocket.isConnected;
export const selectWebSocketError = (state) => state.websocket.error;
export const selectOnlineUsers = (state) => state.websocket.onlineUsers;
export const selectReconnectAttempts = (state) => state.websocket.reconnectAttempts;

export default websocketSlice.reducer;
